/**
 * Import react libraries.
 */
import React from 'react';

/**
 * Import third-party libraries.
 */
import styled from 'styled-components';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import en from 'date-fns/locale/en-GB';
import dayjs from 'dayjs';
import advancedFormat  from 'dayjs/plugin/advancedFormat';
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(advancedFormat);
dayjs.extend(quarterOfYear);

/**
 * Import custom components.
 */
import COLORS from '../styles/colors';
import {SystemIcons} from '../icons';
import {DatepickerFieldHeader} from './DatepickerFieldHeader';
import {ComponentTextStyle, focusStyles, scrollBarStyling} from '../styles';
import TooltipOverflow from '../Tooltips/TooltipOverflow';

/**
 * Import custom styles.
 */
import {ErrorMessage, InputFieldStyling, InputWrapper} from './styling';
import 'react-datepicker/dist/react-datepicker.css';
import {ComponentMStyling, ComponentSStyling} from '../styles';
import {Z_INDEXES} from '../styles';
import {useFocusVisibleRef} from '../common';
import {Size, Testable} from '../types';

/**
 * Add custom types.
 */
export type DatepickerFieldProps = Testable & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size' | 'onBlur'> & {
  value?: Date;
  onChange?: (date: Date) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;

  showQuarterYearPicker?: boolean;
  invalid?: boolean;
  dateFormat?: string;
  validationMessage?: string;
  autoComplete?: string;
  placeholder?: string;
  yearPicker?: boolean;
  yearsBeforeCurrentDate?: number;
  yearsAfterCurrentDate?: number;
  margin?: string;

  overflowTooltipPosition?: 'top' | 'bottom';
};

/**
 * Add custom styles.
 */
const DatePickerContainer = styled.div<{ yearPicker: boolean; margin: string; quarterPicker?: boolean }>`
  .react-datepicker-popper {
    z-index: ${Z_INDEXES.dropdown};
  }

  .react-datepicker-popper[data-placement^=bottom] {
    padding-top: 0px;
  }

  
  .react-datepicker-popper[data-placement^=top] {
    padding-bottom: 0px;
  }

  > div {
    display: block;

    ${(props) => (props.margin ? `margin: ${props.margin};` : '')}
    .react-datepicker {
      box-sizing: border-box;
      box-shadow: 0px 2px 4px rgb(0 0 0 / 15%);
      border: 1px solid #e5e5e5;
    }
    .react-datepicker__input-container{
      z-index: 0;
    }

    .react-datepicker__navigation {
      line-height: normal;
      text-indent: inherit;
      border: none;
      border-radius: 4px;
      height: 48px;
      width: 48px;
      outline: none;
      margin: 0px;
      color: transparent;

      &:hover {
        background: ${COLORS.primary_20};

        &.react-datepicker__navigation--previous::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23215369' /%3E%3C/svg%3E");
        }

        &.react-datepicker__navigation--next::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23215369' /%3E%3C/svg%3E");
        }
      }

      &:focus {
        ${focusStyles}
        &.react-datepicker__navigation--next::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23163746' /%3E%3C/svg%3E");
        }

        &.react-datepicker__navigation--previous::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23163746' /%3E%3C/svg%3E");
        }
      }

      &::before {
        text-align: center;
        display: inline-block;
        content: '';
        height: 32px;
        width: 32px;
        vertical-align: middle;
      }

      &.react-datepicker__navigation--next::before {
        background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23666666' /%3E%3C/svg%3E");
      }

      &.react-datepicker__navigation--previous::before {
        background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23666666' /%3E%3C/svg%3E");
      }
    }

    .react-datepicker__month-container,
    .react-datepicker__year--container {
      width: 336px;
    }

    .react-datepicker__month {
      margin: 0px;
    }

    .react-datepicker__triangle {
      left: 50% !important;
      display: none;
    }

    .react-datepicker-popper[data-placement^='bottom'] {
      margin-top: 0;
    }

    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
      border-bottom-color: ${COLORS.neutral_20};
    }

    .react-datepicker__header {
      border-bottom: 1px solid ${COLORS.neutral_200};
      background: ${COLORS.neutral_20};
      height: ${(props) => (!props.yearPicker ? '96px' : '54px')};
      padding-top: 0px;
    }

    .react-datepicker-year-header {
      height: 48px;
      line-height: 48px !important;
      align-items: center;

      ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
    }

    .react-datepicker__current-month {
      height: 48px;
      display: inline-flex;
      align-items: center;
      margin-right: 5px;

      ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
    }

    .react-datepicker__year {
      max-height: 300px;
      overflow-y: scroll;

      ${scrollBarStyling(Size.Small)}
    }

    .react-datepicker__quarter-wrapper{
      width: 100%;
      display: flex;
      align-content: space-evenly;
      flex-wrap: wrap;
      align-items: center;
      gap: 20px;
    }

    .react-datepicker__year-wrapper {
      max-width: 100%;
      justify-content: space-around;
    }

    .react-datepicker__navigation-icon {
      display: none;
    }

    .react-datepicker__day-name {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 48px;
      margin: 0px;
      width: 48px;

      ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
    }

    

    .react-datepicker__year-text {
      background: ${COLORS.white};
      border: none;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      margin-top: 20px;
      padding-top: 10px;
      padding-bottom: 10px;

      ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
      &.react-datepicker__year-text--disabled {
        display: none;
      }

      &.react-datepicker__day--keyboard-selected {
        color: ${COLORS.neutral_600};
        background: ${COLORS.white};
        font-weight: 400;
        border: none;
      }

      &:hover {
        color: ${COLORS.primary_700};
        background: ${COLORS.primary_20};
      }

      &:focus {
        ${focusStyles}
      }

      &.react-datepicker__year-text--selected {
        color: ${COLORS.white} !important;
        background: ${COLORS.primary_500} !important;
      }
    }

    .react-datepicker__day, .react-datepicker__quarter-text {
      background: ${COLORS.white};
      border: none;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      flex-grow: 1;
      height: 40px;
      width: 40px;
      margin: 4px;

      ${ComponentMStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
      &.react-datepicker__day--keyboard-selected, .react-datepicker__quarter-text--keyboard-selected {
        color: ${COLORS.neutral_600};
        background: ${COLORS.white};
        flex-grow: 1;
        font-weight: 400;
        border: none;
      }

      &.react-datepicker__quarter--selected {
        color: ${COLORS.white} !important;
        flex-grow: 1;
        background: ${COLORS.primary_500} !important;
      }

      &:hover {
        color: ${COLORS.primary_700};
        background: ${COLORS.primary_20};
      }

      &:focus {
        ${focusStyles}
      }

      &.react-datepicker__day--selected {
        color: ${COLORS.white} !important;
        background: ${COLORS.primary_500} !important;
      }

      &.react-datepicker__day--today {
        border-radius: 0.3rem;
        color: ${COLORS.neutral_700};
        background: ${COLORS.neutral_100};

        &:hover {
          color: ${COLORS.primary_700};
          background: ${COLORS.primary_20};
        }

        &:focus {
          ${focusStyles}
        }
      }
    }
  }
`;

const IconWrapper = styled.div`
  pointer-events: none;
  position: absolute;
  left: 16px;
  width: 24px;
  height: 24px;
  z-index: ${+Z_INDEXES.focus + 1};
`;

const DatepickerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledInputFieldStyling = styled(InputFieldStyling)`
  padding-left: 48px !important;
  
  &::placeholder {
    color: ${COLORS.neutral_500};
  }

  &:focus:not(.focus-visible) {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_300};
  }

  &:hover {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_200};

    ::placeholder {
      color: ${COLORS.primary_700};
    }

    cursor: pointer;
    background-color: ${COLORS.primary_20};
    color: ${COLORS.primary_700};
  }

  &:active,
  &.open {
    box-shadow: inset 0px 0px 0px 2px ${COLORS.primary_300};
    background-color: ${COLORS.primary_100};
    color: ${COLORS.primary_800};

    ::placeholder {
      color: ${COLORS.primary_800};
    }
  }
  
  &:disabled {
    background-color: ${COLORS.white};
  }

  & ~ ${IconWrapper}{
    color: ${COLORS.neutral_600};
  }

  &:hover ~ ${IconWrapper} {
    color: ${COLORS.primary_700};
  }

  &:active ~ ${IconWrapper}, &.open ~ ${IconWrapper} {
    color: ${COLORS.primary_800};
  }

  &[tabindex='-1'] ~ ${IconWrapper} {
    color: ${COLORS.neutral_300};
  }
`;

const quarterComponentsClassPrefix = 'react-datepicker__quarter-';

const DatepickerField = React.forwardRef<HTMLInputElement, DatepickerFieldProps>(({
                                            disabled,
                                            readOnly,
                                            onChange,
                                            invalid,
                                            value,
                                            validationMessage,
                                            dateFormat,
                                            autoComplete,
                                            placeholder,
                                            required,
                                            yearPicker,
                                            yearsBeforeCurrentDate,
                                            yearsAfterCurrentDate,
                                            margin,
                                            overflowTooltipPosition,
                                            onBlur,
                                            showQuarterYearPicker,
                                            id,
                                            dataTestId,
                                            ...rest
                                          }: DatepickerFieldProps, ref) => {
  // Globally used variables within the component
  const inputRef = useFocusVisibleRef();
  const datepickerRef = React.useRef<any>(null);
  const focusedQuarterRef = React.useRef<number>(1);
  const [activeMonthPage, setActiveMonthPage] = React.useState<number>(-1);
  const [yearPickerMode, setYearPickerMode] = React.useState<boolean>(false);
  const [openAt, setOpenAt] = React.useState<Date | null>(null);
  // 'open' flag is used only for tracking current state of the dropdown,
  // for explicitly opening/closing the picker we are using 'datepickerRef.current.setOpen'
  const [open, setOpen] = React.useState<boolean>(false);

  const getFormattedValue = () => {
    if(showQuarterYearPicker)
    {
      const val = dayjs(value);
      return val.year() + ', Q' + val.quarter();
    }
    else 
      return dayjs(value).format(dateFormat ? dateFormat : 'MMMM Do, YYYY');
  }
  /**
   * Format the date in a specific way.
   */
  React.useEffect(() => {
    if (value) inputRef.current.value = getFormattedValue();
  }, [value]);

  function getQuarterItem(classname: string) : HTMLElement {
    return document.getElementsByClassName(quarterComponentsClassPrefix + classname)[0] as HTMLElement;
  }

  React.useEffect(() => {
    if (yearPickerMode) {
      //unfortunately, since there are no refs, and year pickers is rendered not by our code, but by 'react-datepicker'
      //we have to touch DOM directly
      const selectedDate = document.getElementsByClassName('react-datepicker__year-text--selected')[0] as HTMLElement;
      const dropdownParent = selectedDate?.parentElement?.parentElement;
      if (selectedDate && dropdownParent) dropdownParent.scrollTop = selectedDate.offsetTop - 2 * selectedDate.offsetHeight;
    }
  }, [yearPickerMode]);

  React.useEffect(() => {
    if(showQuarterYearPicker && open)
    {
      const wrapper = getQuarterItem('wrapper');
      const handler = (event: KeyboardEvent) => {
        switch(event.key)
        {
          case ' ':
          case 'Enter':
            const currentQ = getQuarterItem(focusedQuarterRef.current + '');
            currentQ.click();
            break;

          case 'ArrowLeft':
            if(focusedQuarterRef.current > 1)
            {
              const currentQ = getQuarterItem(focusedQuarterRef.current + '');
              const previousQ = getQuarterItem((focusedQuarterRef.current - 1) + '');
              if(previousQ)
              {
                currentQ.tabIndex = -1;
                previousQ.tabIndex = 0;
                previousQ.focus();
                focusedQuarterRef.current = focusedQuarterRef.current - 1;
              }
            }
            break;

          case 'ArrowRight':
            if(focusedQuarterRef.current < 4)
            {
              const currentQ = getQuarterItem(focusedQuarterRef.current + '');
              const nextQ = getQuarterItem((focusedQuarterRef.current + 1) + '');
              if(nextQ)
              {
                currentQ.tabIndex = -1;
                nextQ.tabIndex = 0;
                nextQ.focus();
                focusedQuarterRef.current = focusedQuarterRef.current + 1;
              }
            }
            break;
        }
      };

      wrapper.addEventListener('keydown', handler);
      //unfortunately since there are no efs, and quarter picker is rendered not by our code, but by 'react-datepicker'
      //we have to touch DOM directly
      
      const selectedQuarter = getQuarterItem('-selected');
      if(selectedQuarter) {
        for (let i = 1; i < 5; i++) {
          if(selectedQuarter.innerText.includes('Q' + i)) {
            focusedQuarterRef.current = i;
            break;
          }
        }
        selectedQuarter.setAttribute("tabIndex", "0");
      }
      else
      {
        const firstQuarter = getQuarterItem('1');
        if(firstQuarter) {
          focusedQuarterRef.current = 1;
          firstQuarter.setAttribute("tabIndex", "0");
        }
      }

      return () => {
        wrapper.removeEventListener('keydown', handler);
      };
    }
  }, [showQuarterYearPicker, open]);

  //we have to manually reopen the calendar when needed, to guarantee that datepicker will show selected date after year selection
  //otherwise after year selection datepicker will show January month of the selected year
  React.useEffect(() => {
    if (openAt) {
      datepickerRef.current.setOpen(true);
      //setOpenAt(null);
    }
  }, [openAt]);

  React.useImperativeHandle(ref, () => inputRef.current, [inputRef]);

  const handleCalendarClose = () => {
    inputRef.current?.blur();
    setOpen(false);
  };

  const handleCalendarOpen = () => {
    if (openAt) setOpenAt(null);
    setOpen(true);
  };

  let maxDate = new Date(value && new Date(value) > new Date() ? new Date(value) : new Date());
  maxDate.setFullYear(maxDate.getFullYear() + (yearsAfterCurrentDate ?? 50));

  //itemsNumber - controls number of year select options generated in the dropdown
  //items related to dates higher than 'maxDate' will be hidden, thus to avoid creating hundreds of invisible divs, we divide by 5
  let minDate = new Date(value && new Date(value) < new Date() ? new Date(value) : new Date());
  const itemsNumber = Math.round((minDate.getFullYear() - (yearsBeforeCurrentDate ?? 15)) / 5);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Esc')
      datepickerRef.current.setOpen(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // @ts-ignore
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onBlur && onBlur(e);
    }
  };
  const iconSize = 24;

  return (
    <>
      {/* Let's render the input itself */}
      <DatePickerContainer quarterPicker={showQuarterYearPicker} data-testid={dataTestId} yearPicker={yearPickerMode} margin={margin || ''} onBlur={handleBlur}>
        <DatePicker
          ref={datepickerRef}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={handleCalendarClose}
          onKeyDown={handleKeyDown}
          id={id}
          onClickOutside={() => datepickerRef.current.setOpen(false)}

          onChange={(e: any) => {
            if (onChange) {
              if (yearPickerMode) {
                const newDate = new Date();
                newDate.setFullYear(e.getFullYear());
                newDate.setMonth(activeMonthPage);
                setOpenAt(newDate);
              } else onChange(e);
            }
            if (yearPickerMode) setYearPickerMode(false);
          }}
          disabled={disabled || readOnly}
          locale={en}
          showQuarterYearPicker={showQuarterYearPicker}
          dateFormat={showQuarterYearPicker ? "yyyy, QQQ" : undefined}
          showYearPicker={yearPickerMode}
          //we manipulate 'yearItemNumber' and 'maxDate' to guarantee number of year select options shown before and after selected year
          yearItemNumber={yearPickerMode ? itemsNumber : undefined}
          maxDate={yearPickerMode ? maxDate : undefined}
          selected={value}
          openToDate={openAt ?? undefined}
          preventOpenOnFocus={true}
          shouldCloseOnSelect={true}
          renderCustomHeader={
            yearPicker
              ? (params: ReactDatePickerCustomHeaderProps) =>
                React.createElement(
                  DatepickerFieldHeader,
                  {
                    ...params,
                    setActiveMonthPage,
                    customHeaderCount: 0,
                    yearPickerMode: yearPickerMode,
                    setYearPickerMode: setYearPickerMode,
                  },
                  null,
                )
              : undefined
          }
          customInput={
            <InputWrapper disabled={disabled} readOnly={readOnly}>
              <DatepickerRow>
                  <StyledInputFieldStyling
                    ref={inputRef}
                    type="text"
                    name="datepicker"
                    onKeyDown={(e) => e.key === 'Enter' && datepickerRef.current.setOpen(true)}
                    className={(invalid ? 'invalid' : '' + (open ? ' open' : ''))}
                    tabIndex={disabled || readOnly ? -1 : 0}
                    onFocus={(e) => inputRef.current?.setSelectionRange(0, 0)}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    extraRightPadding={iconSize}
                    disabled={disabled}
                    readOnly={true}
                    suppressReadOnlyStyles={!readOnly}
                    required={required}
                    {...rest}
                  />
                  <IconWrapper>
                    <SystemIcons.Calendar size="24"/>
                  </IconWrapper>
                </DatepickerRow>
              {!open && <TooltipOverflow position={overflowTooltipPosition} input={inputRef} withArrow={true} maxWidth="100%" size={Size.Small} align='end'>
                {getFormattedValue()}
              </TooltipOverflow>}
            </InputWrapper>
          }
        />
      </DatePickerContainer>

      {/* If there is an error, let's render the error */}
      {validationMessage && (
        <ErrorMessage>
          <SystemIcons.TechnicalWarning color={COLORS.critical_400}/>
          <span>{validationMessage}</span>
        </ErrorMessage>
      )}
    </>
  );
});

export default DatepickerField;
